import { Entity } from '@backstage/catalog-model';
import { WarningPanel } from '@backstage/core-components';

/**
 * The annotation key that TechDocs reads to locate the documentation source.
 * When this annotation is absent, Backstage cannot publish or render docs for
 * the entity and the Docs tab will be empty / stuck loading.
 */
const TECHDOCS_REF_ANNOTATION = 'backstage.io/techdocs-ref';

/**
 * EntitySwitch predicate — returns `true` when the entity does NOT have the
 * `backstage.io/techdocs-ref` annotation.
 *
 * Usage:
 * ```tsx
 * <EntitySwitch.Case if={e => isKind('component')(e) && isTechDocsMissing(e)}>
 *   <Grid item xs={12}><TechDocsAnnotationWarning /></Grid>
 * </EntitySwitch.Case>
 * ```
 */
export const isTechDocsMissing = (entity: Entity): boolean =>
  !entity.metadata.annotations?.[TECHDOCS_REF_ANNOTATION];

/**
 * Warning card displayed on the entity Overview tab when the entity has not
 * been configured for TechDocs.
 *
 * Drop this inside an `EntitySwitch.Case` guarded by {@link isTechDocsMissing}
 * so it only renders when the annotation is actually absent.
 *
 * The component renders a `WarningPanel` (the same component used by
 * `EntityOrphanWarning`) so it visually integrates with the existing warning
 * strip at the top of every Overview page.
 */
export const TechDocsAnnotationWarning = () => (
  <WarningPanel
    severity="warning"
    title="TechDocs not configured for this entity"
    message={
      <>
        Add a <code>backstage.io/techdocs-ref</code> annotation to{' '}
        <code>catalog-info.yaml</code> so the Docs tab can render content from
        the published MkDocs site. Use one of the platform templates (e.g.{' '}
        <em>Create Microservice</em>) to scaffold the required{' '}
        <code>docs/</code> folder and <code>mkdocs.yml</code> automatically.
      </>
    }
  />
);
